/**
 * All known feature flags an account can have
 */
export const FeatureEnum = {
  TWO_FA:                 '2FA',
  ADMIN:                  'admin',
  ANALYTICS:              'analytics',
  ASSISTANT:              'assistant',
  CONVERSATIONAL_VIEW:    'conversationalView',
  DASHBOARD:              'dashboard',
  DIALOGS:                'dialogs',
  FILE_ACCESS:            'file-access',
  FORM_VIEW:              'formView',
  LIVECHAT:               'livechat',
  MULTIBOT:               'multibot',
  MUTE:                   'mute',
  OWNER:                  'owner',
  PREVIEW:                'preview',
  RAG:                    'rag',
  RAG_CHARACTER_COUNT:    'ragCharacterCount',
  RAG_OUTPUT_GATE:        'ragOutputGate',
  SIMPLE_EDIT:            'simpleEdit',
  SSO_2FA:                'sso2FA',
  STAGING:                'staging',
  SUGGESTION:             'suggestion',
  SUPERVISOR:             'supervisor',
  SIPGATE:                'sipgate',
  WIDGET_EDITOR_VIEW:     'widgetEditorView',

  /**
   * ------------------------------
   * ## Adding a new feature flag?
   * ------------------------------
   *
   * If the feature should be kept on the user as they move between roles,
   * make sure you add it to the `inheritableFlags` array in the
   * `mergeUserFeatures` function.
   *
   * In some cases, it is valid to keep a feature flag even though the user
   * cannot use it in their new role. This is for UX when they are moved back.
   */

  /** @deprecated - Only used in hub? */
  CHAT_INSIGHTS:          'chatInsights',
  /** @deprecated - Only used in hub? */
  NO_BEAMER:              'noBeamer',
  /** @deprecated - Not sure if used anymore (not found in API) */
  BOT_OWNER:              'bot-owner',
  /** @deprecated - Not sure if used anymore (not found in API) */
  WIDGETEDITOR:           'widgeteditor',
  /** @deprecated - Not sure if used anymore (not found in API) */
  MEETUP:                 'meetup',
  /** @deprecated - Not sure if used anymore (not checked) */
  NLP:                    'nlp',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  BOT_SETTINGS:           'botsettings',
  /** @deprecated - Not sure if used anymore (not found in API) */
  CHANNEL_EDIT:           'channelEdit',
  /** @deprecated - Not sure if used anymore (not checked) */
  CHANNELS:               'channels',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  EDIT_ACTIONS:           'editactions',
  /** @deprecated - Not sure if used anymore (not checked) */
  FAQ:                    'faq',
  /** @deprecated - Not sure if used anymore (not found in API) */
  GROWTH:                 'growth',
  /** @deprecated - Not sure if used anymore (not found in API) */
  HUBSPOT:                'hubspot',
  /** @deprecated - Not sure if used anymore (not found in API) */
  NEW_INTENT:             'newIntent',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  NEWS:                   'news',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  NOTIFICATIONS:          'notifications',
  /** @deprecated - Not sure if used anymore (not found in API) */
  ONLY_STAGING:           'onlyStaging',
  /** @deprecated - Not sure if used anymore (not found in API) */
  RESPOND_TO_COMMENTS:    'respondtocomments',
  /** @deprecated - Not sure if used anymore (not found in API) */
  RITTERSPORT:            'rittersport',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  FB_CONNECT:             'fbconnect',
  /** @deprecated - Not sure if used anymore (only found in samples) */
  TIPICO:                 'tipico',
} as const;

/** One of the feature-flag string values of {@link FeatureEnum} (e.g. '2FA', 'admin'). */
export type FeatureEnum = (typeof FeatureEnum)[keyof typeof FeatureEnum];

/**
 * The possible roles a user can be
 * Bounded to userRoles in locales/de.json | locales/en.json
 */
export const rolesTypes = {
  TESTER: 'tester',
  VIEWER: 'viewer',
  EDITOR: 'editor',
  OWNER: 'owner',
  ADMIN: 'admin',
  /** @deprecated - Not sure what we used this for */
  INDIVIDUAL: 'individual',
  /** @deprecated - Not sure what we used this for */
  ASSISTANT: 'assistant',
  /** @deprecated - Not sure what we used this for */
  RAG: 'rag',
} as const;

/** One of the role string values of {@link rolesTypes} (e.g. 'viewer', 'owner'). */
export type RoleType = (typeof rolesTypes)[keyof typeof rolesTypes];

const featuresTester: FeatureEnum[] = [FeatureEnum.PREVIEW];

const featuresViewer: FeatureEnum[] = [
  FeatureEnum.DASHBOARD,
  FeatureEnum.ANALYTICS,
  FeatureEnum.DIALOGS,
  FeatureEnum.SUGGESTION,
  FeatureEnum.CONVERSATIONAL_VIEW,
  FeatureEnum.FORM_VIEW,
  FeatureEnum.WIDGET_EDITOR_VIEW,
];

const featuresEditor: FeatureEnum[] = [...featuresViewer, FeatureEnum.SIMPLE_EDIT];
const featuresOwner: FeatureEnum[] = [...featuresViewer, FeatureEnum.OWNER];

/** Role → default feature set for that role (only assignable roles are listed). */
const featuresLists: Record<string, FeatureEnum[]> = {
  [rolesTypes.TESTER]: featuresTester,
  [rolesTypes.VIEWER]: featuresViewer,
  [rolesTypes.EDITOR]: featuresEditor,
  [rolesTypes.OWNER]: featuresOwner,
};

/**
 * Gets the features that a user should receive for a given role.
 *
 * ⚠️ **NOTE:** If the user already exists, and they get assigned a new role,
 * use the `mergeUserFeatures` function instead.
 * @param roleType role to look up (roles without a default feature set — e.g. admin — yield null)
 * @returns a fresh copy of the role's default features, or null for unknown roles
 */
export function getFeaturesByRole(roleType: RoleType | string): FeatureEnum[] | null {
  if (!roleType || !featuresLists[roleType]) return null;
  return [...featuresLists[roleType]!];
}

/**
 * Detects which role the user has based on the features assigned to them
 * @param features the feature flags currently on the user
 * @returns Undefined if a role could not be determined
 */
export function getRoleByFeatures(features: FeatureEnum[] | null | undefined): RoleType | undefined {
  if (!features) return undefined;

  if (features.includes(FeatureEnum.PREVIEW)) return rolesTypes.TESTER;

  if (features.includes(FeatureEnum.ADMIN)) {
    return rolesTypes.ADMIN;
  }
  if (features.includes(FeatureEnum.OWNER)) {
    return rolesTypes.OWNER;
  }
  if (features.includes(FeatureEnum.SIMPLE_EDIT)) {
    return rolesTypes.EDITOR;
  }
  return rolesTypes.VIEWER;
}

/**
 * @todo TODO: Löschen wenn nicht mehr gebraucht
 * @param features the feature flags currently on the user
 * @returns detected role, or INDIVIDUAL when no default feature set matches exactly
 */
export function getRoleByFeaturesDeprecated(features: FeatureEnum[]): RoleType {
  // features to test without '2FA' and 'admin'
  const featuresOriginal = [...features];
  deleteFeatureInList(FeatureEnum.TWO_FA, featuresOriginal);
  deleteFeatureInList(FeatureEnum.ADMIN, featuresOriginal);
  // 2nd list to delete features while testing
  const featuresTest = [...featuresOriginal];
  function deleteFeatureInList(feature: FeatureEnum, list: FeatureEnum[]): void {
    const index = list.findIndex((f) => f === feature);
    if (index >= 0) {
      list.splice(index, 1);
    }
  }

  function deleteFeaturesAndTest(roleType: RoleType): boolean {
    // Non-null: only called with roles present in featuresLists
    featuresLists[roleType]!.map((f) => deleteFeatureInList(f, featuresTest));
    if (
      featuresOriginal.length === featuresLists[roleType]!.length &&
      featuresTest.length === 0
    ) {
      return true;
    }
    return false;
  }

  if (deleteFeaturesAndTest(rolesTypes.VIEWER)) {
    return rolesTypes.VIEWER;
  }
  if (deleteFeaturesAndTest(rolesTypes.EDITOR)) {
    return rolesTypes.EDITOR;
  }
  if (deleteFeaturesAndTest(rolesTypes.OWNER)) {
    return rolesTypes.OWNER;
  }

  return rolesTypes.INDIVIDUAL;
}

/**
 * A function that deals with "migrating" the user's flags when their role gets promoted or demoted.
 * @see https://github.com/knowhereto/moin-hub/issues/2226
 * @param oldUserFlags The old/existing features the user had
 * @param newRole The new role they will have, from which we can derive the features that role should have
 * @param overrides Additional features to always include or always remove. Remove is applied last.
 * @returns The new user flags they should have
 */
export function mergeUserFeatures(
  oldUserFlags: FeatureEnum[] | null | undefined,
  newRole: RoleType | string,
  overrides: { add?: FeatureEnum[]; remove?: FeatureEnum[] } = {add: [], remove: []},
): FeatureEnum[] {
  if (!overrides.add) overrides.add = [];
  if (!overrides.remove) overrides.remove = [];
  if (!oldUserFlags) oldUserFlags = [];

  /**
   * These are the flags that the user can take with them as they move laterally between roles.
   * We keep them even though they might not be able to use them in the new role because for UX
   * if the user gets their role moved back to what it was.
   */
  const inheritableFlags: FeatureEnum[] = [
    FeatureEnum.SSO_2FA,
    FeatureEnum.TWO_FA,
    FeatureEnum.ADMIN,
    FeatureEnum.MULTIBOT,
    FeatureEnum.ASSISTANT,
    FeatureEnum.RAG,
    FeatureEnum.RAG_OUTPUT_GATE,
    // access to livechat
    FeatureEnum.LIVECHAT,
    // can read files
    FeatureEnum.FILE_ACCESS,
    // supervisor (livechat)
    FeatureEnum.SUPERVISOR,
    // Resource Character Counts (KB)
    FeatureEnum.RAG_CHARACTER_COUNT,
    // Sipgate integration
    FeatureEnum.SIPGATE,
  ];

  const newFeatures = getFeaturesByRole(newRole) || [];
  const canKeep = oldUserFlags.filter((feature) => inheritableFlags.includes(feature));

  const combined = canKeep
    .concat(overrides.add)
    .concat(newFeatures)
    .filter(feature => !overrides.remove!.includes(feature));

  return Array.from( new Set(combined) );
}
